import * as React from 'react';
import Tabs from 'antd/lib/tabs';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import { ReleaseTypesForNonBlockingSteps } from '@/types';
import { displayTextForReleaseTypeGroup } from '@/helpers/displayHelpers';
import { antGrey, runwayColors } from './styles/globalStyles';

export const ALL_RELEASE_TYPE_GROUPS: ReleaseTypesForNonBlockingSteps[] = [
    ReleaseTypesForNonBlockingSteps.Standard,
    ReleaseTypesForNonBlockingSteps.Hotfix,
    ReleaseTypesForNonBlockingSteps.Rollback,
];

export interface ReleaseTypeTab {
    id: string;
    releaseTypes: ReleaseTypesForNonBlockingSteps[];
}

export const groupReleaseTypesByValue = <T,>(
    valueForReleaseType: (releaseType: ReleaseTypesForNonBlockingSteps) => T,
): { releaseTypes: ReleaseTypesForNonBlockingSteps[]; value: T }[] => {
    const groups = new Map<string, { releaseTypes: ReleaseTypesForNonBlockingSteps[]; value: T }>();
    for (const releaseType of ALL_RELEASE_TYPE_GROUPS) {
        const value = valueForReleaseType(releaseType);
        const fingerprint = JSON.stringify(value);
        const group = groups.get(fingerprint);
        if (group) {
            group.releaseTypes.push(releaseType);
        } else {
            groups.set(fingerprint, { releaseTypes: [releaseType], value });
        }
    }
    return Array.from(groups.values());
};

export const releaseTypeTabTitle = (releaseTypes: ReleaseTypesForNonBlockingSteps[]): string => {
    if (releaseTypes.length === 0) return 'New configuration';
    return [...releaseTypes]
        .sort((a, b) => ALL_RELEASE_TYPE_GROUPS.indexOf(a) - ALL_RELEASE_TYPE_GROUPS.indexOf(b))
        .map(displayTextForReleaseTypeGroup)
        .join(', ');
};

const tabContentStyle: React.CSSProperties = {
    background: runwayColors.white,
    borderRadius: '0 0 8px 8px',
    padding: 16,
    border: `1px solid ${antGrey.grey4}`,
    borderTop: 'none',
};

const emptyTabContentStyle: React.CSSProperties = {
    opacity: 0.5,
    filter: 'grayscale(100%)',
    pointerEvents: 'none',
};

interface ReleaseTypeTabsProps {
    tabs: ReleaseTypeTab[];
    onTabsChange: (tabs: ReleaseTypeTab[]) => void;
    activeTabId: string;
    onActiveTabChange: (tabId: string) => void;
    onTabSplit: (sourceTabId: string, newTabId: string) => void;
    renderTabContent: (tab: ReleaseTypeTab) => React.ReactNode;
}

export const ReleaseTypeTabs: React.FC<ReleaseTypeTabsProps> = (props: ReleaseTypeTabsProps) => {
    const { tabs, onTabsChange, activeTabId, onActiveTabChange, onTabSplit, renderTabContent } = props;
    const nextTabIndex = React.useRef(tabs.length);
    const newTabId = () => `tab-${nextTabIndex.current++}`;

    const handleReleaseTypeChange = (tabIndex: number, selected: ReleaseTypesForNonBlockingSteps[]) => {
        const currentTab = tabs[tabIndex];
        const removed = currentTab.releaseTypes.filter((t) => !selected.includes(t));

        const updated = tabs.map((tab, index) => ({
            ...tab,
            releaseTypes: index === tabIndex ? selected : tab.releaseTypes.filter((t) => !selected.includes(t)),
        }));

        for (const removedType of removed) {
            const id = newTabId();
            onTabSplit(currentTab.id, id);
            updated.push({ id, releaseTypes: [removedType] });
        }

        onTabsChange(updated.filter((tab) => tab.releaseTypes.length > 0 || tab.id === currentTab.id));
        onActiveTabChange(currentTab.id);
    };

    const handleAddTab = () => {
        const id = newTabId();
        onTabsChange([...tabs, { id, releaseTypes: [] }]);
        onActiveTabChange(id);
    };

    const handleCloseTab = (tabId: string) => {
        const tabToClose = tabs.find((t) => t.id === tabId);
        const remaining = tabs.filter((t) => t.id !== tabId);
        if (!tabToClose || remaining.length === 0) return;

        const last = remaining[remaining.length - 1];
        remaining[remaining.length - 1] = {
            ...last,
            releaseTypes: Array.from(new Set([...last.releaseTypes, ...tabToClose.releaseTypes])),
        };
        onTabsChange(remaining);
        onActiveTabChange(last.id);
    };

    return (
        <Tabs
            activeKey={activeTabId}
            onChange={onActiveTabChange}
            type="editable-card"
            tabBarStyle={{ marginBottom: 0 }}
            onEdit={(targetKey, action) => {
                if (action === 'add') {
                    handleAddTab();
                } else if (action === 'remove' && typeof targetKey === 'string') {
                    handleCloseTab(targetKey);
                }
            }}
            items={tabs.map((tab, index) => ({
                key: tab.id,
                label: releaseTypeTabTitle(tab.releaseTypes),
                closable: tabs.length > 1,
                forceRender: true,
                children: (
                    <div style={tabContentStyle}>
                        <Form.Item label="Release types" style={{ marginBottom: 16 }}>
                            <Select
                                mode="multiple"
                                value={tab.releaseTypes}
                                onChange={(selected) => handleReleaseTypeChange(index, selected)}
                                options={ALL_RELEASE_TYPE_GROUPS.map((t) => ({
                                    value: t,
                                    label: displayTextForReleaseTypeGroup(t),
                                }))}
                            />
                        </Form.Item>
                        <div style={index > 0 && tab.releaseTypes.length === 0 ? emptyTabContentStyle : undefined}>
                            {renderTabContent(tab)}
                        </div>
                    </div>
                ),
            }))}
        />
    );
};
